import React from 'react';
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { LifeEventType } from '../../types/person';
import { TimelineEvent } from '../../types/timeline';

interface EventTypeFilterProps {
  events: TimelineEvent[];
  selectedTypes: LifeEventType[];
  onChange: (types: LifeEventType[]) => void;
}

const EVENT_TYPE_LABELS: Record<LifeEventType, string> = {
  birth: 'Birth',
  death: 'Death',
  marriage: 'Marriage',
  divorce: 'Divorce',
  education: 'Education',
  employment: 'Employment',
  military: 'Military Service',
  immigration: 'Immigration',
  residence: 'Residence',
  achievement: 'Achievement',
  other: 'Other Events',
};

// Event types sorted alphabetically by label
const ALL_EVENT_TYPES: LifeEventType[] = [
  'achievement',     // Achievement
  'birth',          // Birth
  'death',          // Death
  'divorce',        // Divorce
  'education',      // Education
  'employment',     // Employment
  'immigration',    // Immigration
  'marriage',       // Marriage
  'military',       // Military Service
  'other',          // Other Events
  'residence',      // Residence
];

/**
 * EventTypeFilter Component
 * Filter timeline events by type with count badges
 * Part of Phase 3.3: Interactive Timeline Page
 */
export const EventTypeFilter: React.FC<EventTypeFilterProps> = ({
  events,
  selectedTypes,
  onChange,
}) => {
  // Calculate event counts by type
  const eventCounts = React.useMemo(() => {
    const counts: Record<LifeEventType, number> = {} as Record<LifeEventType, number>;

    ALL_EVENT_TYPES.forEach(type => {
      counts[type] = events.filter(e => e.type === type).length;
    });

    return counts;
  }, [events]);

  const handleToggleType = (type: LifeEventType) => {
    if (selectedTypes.includes(type)) {
      onChange(selectedTypes.filter(t => t !== type));
    } else {
      onChange([...selectedTypes, type]);
    }
  };

  const handleSelectAll = () => {
    onChange(ALL_EVENT_TYPES);
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Event Types
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {selectedTypes.length} / {ALL_EVENT_TYPES.length} selected
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleSelectAll}
          disabled={selectedTypes.length === ALL_EVENT_TYPES.length}
        >
          Select All
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleDeselectAll}
          disabled={selectedTypes.length === 0}
        >
          Clear
        </Button>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Event Type Checkboxes with Count Badges */}
      <FormGroup>
        {ALL_EVENT_TYPES.map(type => {
          const count = eventCounts[type] || 0;
          const isDisabled = count === 0;

          return (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  checked={selectedTypes.includes(type)}
                  onChange={() => handleToggleType(type)}
                  disabled={isDisabled}
                  size="small"
                />
              }
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    opacity: isDisabled ? 0.5 : 1,
                  }}
                >
                  <Typography variant="body2">{EVENT_TYPE_LABELS[type]}</Typography>
                  <Chip
                    label={count}
                    size="small"
                    color={count > 0 ? 'primary' : 'default'}
                    sx={{
                      ml: 1,
                      height: '20px',
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              }
              sx={{ mb: 0.5 }}
            />
          );
        })}
      </FormGroup>
    </Box>
  );
};

export default EventTypeFilter;
